// @ts-expect-error
import Cookies from "/modules/vendor/js-cookie.js";

import { MessageType } from "/modules/components/notifier.js";
import { config } from "/modules/config.js";
import { systempb, userpb } from "/modules/rpc.js";
import { RPCError } from "/modules/rpc-error.js";
import { sessionStorageUtil } from "/modules/session-storage-util.js";
import { localStorageUtil } from "/modules/local-storage-util.js";
import { jwtParse } from "/modules/jwt-parse.js";

import { util } from "/modules/util.js";
import "/modules/components/dialog.js";
import tailwind from "/modules/tailwind.css.js";

import { html, HTMLTemplateResult } from "/modules/vendor/lit.js";
import { BaseElement } from "/modules/components/base-element.js";
import { customElement, query, state } from "/modules/vendor/lit/decorators.js";
import { RequestTwoFactorAuthResetAction } from "/modules/components/account/request-two-factor-auth-reset-action.js";
import "/modules/components/account/request-two-factor-auth-reset-action.js";
import "/modules/components/page/modal-portal.js";
import {
	LoginSecondFactor,
	LoginSecondFactorChosenEvent,
	LoginTwoFactorAuthCodeSubmittedEvent,
} from "/modules/events.js";
import { LoginSecondFactorChoiceDialog } from "/modules/components/login/login-second-factor-choice-dialog.js";
import { LoginTwoFactorCodeDialog } from "/modules/components/login/login-two-factor-code-dialog.js";
import "/modules/components/login/login-second-factor-choice-dialog.js";
import "/modules/components/login/login-two-factor-code-dialog.js";
import timedCache from "/modules/timed-cache.js";
import "/modules/components/login-container/login-view-container.js";
import { FormWrapperImageType } from "/modules/components/login-container/login-view-container.js";
import {
	BackendError,
	newBackendError,
} from "/modules/components/login-container/backend-error.js";

declare global {
	interface HTMLElementTagNameMap {
		"komit-login-view": LoginView;
	}
}

// the login page doubles as the landing page of the magic link mailed by
// "login confirmation by email"
enum MagicLinkStep {
	// no magic link involved, the ordinary login form
	None,
	// the link was opened and is being redeemed
	Confirming,
	// password accepted, the link was mailed
	EmailSent,
	// the link could not be redeemed, for example in the wrong browser
	Failed,
}

@customElement("komit-login-view")
export class LoginView extends BaseElement {
	@state()
	private isOpen = false;

	@state()
	private magicLinkStep = MagicLinkStep.None;

	@state()
	private backendErrors: BackendError[] = [];

	@state()
	private isTotpAvailable = false;

	@state()
	private isEmailMagicLinkAvailable = false;

	// which factor the user is completing the login with. no template reads it,
	// it decides where a failure is shown and keeps a repeated availability
	// response from throwing the user back to a choice already made
	private chosenSecondFactor: LoginSecondFactor | undefined;

	@query("#email")
	email: HTMLInputElement | undefined;

	@query("#password")
	password: HTMLInputElement | undefined;

	@query("#resetPasswordEmail")
	resetPasswordEmail: HTMLInputElement | undefined;

	@query("#secondFactorChoiceDialog")
	secondFactorChoiceDialog: LoginSecondFactorChoiceDialog | undefined;

	@query("#twoFactorCodeDialog")
	twoFactorCodeDialog: LoginTwoFactorCodeDialog | undefined;

	@query("#twoFactorAuthResetAction")
	twoFactorAuthResetAction: RequestTwoFactorAuthResetAction | undefined;

	connectedCallback() {
		super.connectedCallback();

		const magicToken = new URL(window.location.href).searchParams.get(
			"magic_token",
		);
		if (magicToken) {
			// the token must not survive in the address bar, the history or a
			// referrer. also skips the redirect below, which would otherwise send a
			// browser holding an old session straight to the dashboard as the old
			// user, without redeeming the link
			history.replaceState(null, "", window.location.pathname);

			// the link belongs to the account this browser is already signed in as,
			// so it was a way into the app rather than a way to log in - the mail
			// reopened days later, or after closing the browser on a link already
			// followed. redeeming it would answer "already used" to someone holding
			// a working session
			if (this.isSignedInAsMagicLinkAccount(magicToken)) {
				this.redirect(null);
				return;
			}

			this.confirmMagicLink(magicToken);
			return;
		}

		if (
			Cookies.get("ExpirationTime") != undefined &&
			window.location.pathname == "/"
		) {
			this.redirect(null);
		}

		timedCache
			.cacheRequest(
				2000,
				"config.rpcClient.SystemSettingService.appStatus",
				systempb.AppStatusReq.create({}),
				config.rpcClient.SystemSettingService.appStatus.bind(
					config.rpcClient.SystemSettingService,
				),
			)
			.then((resp) => {
				const url = new URL(window.location.href);
				const email = url.searchParams.get("email");
				const password = url.searchParams.get("password");

				if (resp.isFirstLoginAlreadyHappened === false) {
					if (email && password && this.email && this.password) {
						this.email.value = email;
						this.password.value = password;
					}
				} else if (email && this.email) {
					this.email.value = email;
				}
			});
	}

	// necessary to make login work with some password managers
	// see https://github.com/komit-ag/komit-dev/issues/91 for details
	protected createRenderRoot() {
		return this;
	}

	// the claims of the magic token, read without verifying anything: the payload
	// is the middle segment of the JWT and needs no key. nothing is granted on
	// the workspace the link was minted for, by hostname. the port is left out:
	// the same workspace answers on the plain and the TLS port, and the backend
	// URL a link carries is whichever one it was configured with
	private isSameWorkspace(workspaceURL: unknown): boolean {
		if (typeof workspaceURL !== "string" || workspaceURL === "") {
			return false;
		}

		try {
			return new URL(workspaceURL).hostname === window.location.hostname;
		} catch {
			return false;
		}
	}

	// two claims decide it: the link names this workspace, and it names the
	// account this browser is signed in as. the account copy is written and
	// cleared together with the session - login stores it, logout drops it - so a
	// match means the link and the live session belong to the same person.
	//
	// the claims are read unverified, which is sound because nothing is granted
	// here: this only decides whether the browser still has to log in, and the
	// session it already holds is what every later request is judged by. a token
	// this browser cannot read goes to the server, which is where an unreadable
	// link belongs
	private isSignedInAsMagicLinkAccount(magicToken: string): boolean {
		if (Cookies.get("ExpirationTime") == undefined) {
			return false;
		}

		const accountID = localStorageUtil.account()?.id?.value;
		if (accountID == undefined) {
			return false;
		}

		const claims = jwtParse(magicToken);
		if (!claims) {
			return false;
		}

		return (
			this.isSameWorkspace(claims["MagicLinkWorkspaceURL"]) &&
			String(claims["MagicLinkAccountID"]) === String(accountID)
		);
	}

	private redirect(redirectPath: string | null) {
		if (sessionStorageUtil.isRestrictedToAuthService()) {
			window.location.href = "/settings/";
		} else {
			if (redirectPath) {
				window.location.href = redirectPath;
				return;
			}

			window.location.href = "/dashboard/";
		}
	}

	login(e: CustomEvent) {
		e.preventDefault();

		// a fresh ceremony: whatever was picked for the previous one is stale
		this.chosenSecondFactor = undefined;

		this.submitLogin(
			userpb.LoginAuthReq.create({
				email: this.email?.value,
				password: this.password?.value,
			}),
		);
	}

	// every login call goes through here, so there is one place that routes the
	// answer and one place that shows a failure
	private submitLogin(req: userpb.LoginAuthReq) {
		config.rpcClient.AuthService.login(req)
			.then(async (resp) => {
				await this.handleLoginResponse(resp);
			})
			.catch((error: unknown) => {
				this.showLoginFailure(error);
			});
	}

	private async handleLoginResponse(resp: userpb.LoginAuthResp) {
		// the password was accepted and a confirmation link was mailed, so the
		// login is not finished and resp.account is not set
		if (resp.isEmailConfirmationRequired) {
			this.secondFactorChoiceDialog?.close();
			this.twoFactorCodeDialog?.close();
			this.magicLinkStep = MagicLinkStep.EmailSent;
			return;
		}

		// the password was accepted and a second factor is still outstanding. the
		// response carries one flag per factor that can complete this login, so
		// counting them decides: one goes straight to its flow, more than one asks
		const availableFactors = this.availableSecondFactors(resp);
		if (availableFactors.length > 0) {
			await this.openSecondFactor(availableFactors);
			return;
		}

		await this.completeLogin(resp);
	}

	private availableSecondFactors(
		resp: userpb.LoginAuthResp,
	): LoginSecondFactor[] {
		const available: LoginSecondFactor[] = [];

		if (resp.isTwoFactorAuthTotpAvailable) {
			available.push("totp");
		}

		if (resp.isEmailMagicLinkLoginAvailable) {
			available.push("emailMagicLink");
		}

		return available;
	}

	private async openSecondFactor(available: LoginSecondFactor[]) {
		// an answer that repeats the availability - a code field submitted empty
		// would produce one - must not discard a choice the user already made
		const factor =
			this.chosenSecondFactor ??
			(available.length === 1 ? available[0] : undefined);

		if (factor !== undefined) {
			this.startSecondFactor(factor);
			return;
		}

		this.isTotpAvailable = available.includes("totp");
		this.isEmailMagicLinkAvailable = available.includes("emailMagicLink");

		// the dialog reads both as properties, so it can only be opened once this
		// render has reached it
		await this.updateComplete;

		this.secondFactorChoiceDialog?.open();
	}

	private startSecondFactor(factor: LoginSecondFactor) {
		this.chosenSecondFactor = factor;

		if (factor === "emailMagicLink") {
			// the chooser stays open until the mail is out: a refused send, for
			// example while the one-per-minute cooldown runs, is shown in it
			this.submitLogin(
				userpb.LoginAuthReq.create({
					email: this.email?.value,
					password: this.password?.value,
					isEmailMagicLinkConfirmationRequested: true,
				}),
			);
			return;
		}

		this.secondFactorChoiceDialog?.close();
		this.twoFactorCodeDialog?.open();
	}

	private handleSecondFactorChosen(e: LoginSecondFactorChosenEvent) {
		this.startSecondFactor(e.detail.factor);
	}

	// the dialog has already closed itself by the time this arrives, so the reset
	// form has nothing above it
	private handleTwoFactorAuthResetRequested() {
		this.twoFactorAuthResetAction?.openDialog();
	}

	private handleTwoFactorAuthCodeSubmitted(
		e: LoginTwoFactorAuthCodeSubmittedEvent,
	) {
		this.submitLogin(
			userpb.LoginAuthReq.create({
				email: this.email?.value,
				password: this.password?.value,
				twoFactorAuthCode: e.detail.code,
			}),
		);
	}

	// a modal dialog is in the top layer and the notifier is not, so a toast
	// raised behind an open dialog is invisible. while one is up it carries the
	// failure itself; with none open the toast rpc-client raises is what shows -
	// except for a 403, which rpc-client swallows without notifying. the login
	// path therefore returns 400 for every refusal the user is meant to read
	private showLoginFailure(error: unknown) {
		if (this.chosenSecondFactor === "totp") {
			this.twoFactorCodeDialog?.showError(
				this.serverErrorText(
					error,
					util.tr("Login failed, please try it again."),
				),
			);
			return;
		}

		if (this.chosenSecondFactor === "emailMagicLink") {
			this.secondFactorChoiceDialog?.showError(
				this.serverErrorText(
					error,
					util.tr("Login failed, please try it again."),
				),
			);
		}
	}

	// shared by the password login and the magic link redemption
	private async completeLogin(resp: userpb.LoginAuthResp) {
		const externalPath = sessionStorageUtil.get<string>(
			// external path will be used for redirection after login
			// instead of default (currently /dashboard/)
			sessionStorageUtil.externalURLPathToRedirectAfterLoginKey,
		);

		// shouldn't be necessary because done on logout, just for safety for cases
		// when for some reason not triggered on logout
		// UPD: must stay here to remove externalURLPathToRedirectAfterLoginKey
		sessionStorage.clear();

		sessionStorageUtil.setIsRestrictedToAuthService(
			resp.isRestrictedToAuthService,
		);

		// also switches the UI language, which every server generated form reads
		await util.setAccount(resp.account);

		// redirect to external path if present
		this.redirect(externalPath);
	}

	// the binding cookie is HttpOnly, so whether this browser is the one that
	// started the login can only be decided by the server
	private confirmMagicLink(magicToken: string) {
		this.magicLinkStep = MagicLinkStep.Confirming;

		// the try covers everything that can throw before the promise exists:
		// Confirming has no exit control, so a synchronous throw would strand the
		// page on "Completing your login" with no error and no way back
		try {
			let req = userpb.ConfirmMagicLinkAuthReq.create({
				magicToken: magicToken,
			});

			config.rpcClient.AuthService.confirmMagicLink(req)
				.then(async (resp) => {
					await this.completeLogin(resp);
				})
				.catch((error: unknown) => {
					this.showRedemptionFailure(error);
				});
		} catch (error: unknown) {
			this.showRedemptionFailure(error);
		}
	}

	private showRedemptionFailure(error: unknown) {
		this.magicLinkStep = MagicLinkStep.Failed;
		this.backendErrors = [
			newBackendError(this.redemptionFailureText(error)),
		];
	}

	// only an RPCError carries text from the server, and rpc-client already ran
	// that English through util.trx, so it arrives in the user's language and
	// must not be translated a second time. any other throw is client-side and
	// its message is internal, so it falls back rather than reaching the user
	private serverErrorText(error: unknown, fallback: string): string {
		const serverMessage =
			error instanceof RPCError ? error.message.trim() : "";
		if (serverMessage !== "") {
			return serverMessage;
		}

		return fallback;
	}

	private redemptionFailureText(error: unknown): string {
		return this.serverErrorText(
			error,
			util.tr("This login link is no longer valid. Please log in again."),
		);
	}

	private backToLogin() {
		this.magicLinkStep = MagicLinkStep.None;
		this.backendErrors = [];
	}

	// the container only reports which errors are left, writing them back is the
	// host's job
	private updateBackendErrors(e: CustomEvent) {
		this.backendErrors = e.detail;
	}

	requestNewPassword(e: CustomEvent) {
		e.preventDefault();

		let req = userpb.ResetPasswordAuthReq.create({
			email: this.resetPasswordEmail?.value,
		});

		config.rpcClient.AuthService.resetPassword(req).then(() => {
			this.closingDialog();

			util.notify(
				MessageType.Info,
				util.tr(
					"If the email address is registered, you will receive a new password by email.",
				),
			);
		});
	}

	openPasswordForgottenDialog() {
		if (this.resetPasswordEmail && this.email) {
			this.resetPasswordEmail.value = this.email.value;
		}
		this.isOpen = true;
	}

	closingDialog() {
		this.isOpen = false;
	}

	// TODO use <komit-form-dialog> instead of custom form for password reset
	// TODO use FormUtil.load() or better impl new tag for login form generation
	render() {
		switch (this.magicLinkStep) {
			case MagicLinkStep.Confirming:
				return this.magicLinkNoticeTmpl(
					util.tr("Completing your login"),
					util.tr("One moment, please."),
					html``,
				);
			case MagicLinkStep.EmailSent:
				return this.magicLinkNoticeTmpl(
					util.tr("Check your mailbox"),
					util.tr(
						"We sent you a link to complete your login. Open it in this browser, it only works here.",
					),
					html``,
				);
			case MagicLinkStep.Failed:
				return this.magicLinkNoticeTmpl(
					util.tr("Login could not be completed"),
					util.tr("Please log in again to receive a new link."),
					html`
						<div class="pt-1 text-left">
							<button
								@click="${this.backToLogin}"
								class="cursor-pointer w-full px-4 py-3 bg-blue-power-900 hover:bg-blue-power-800 rounded-xl text-xl font-semibold text-white"
								type="button"
							>
								${util.tr("Back to login")}
							</button>
						</div>
					`,
				);
			case MagicLinkStep.None:
				return this.loginFormTmpl();
		}
	}

	private magicLinkNoticeTmpl(
		title: string,
		subtitle: string,
		action: HTMLTemplateResult,
	) {
		return html`
			<style>
				${tailwind}
			</style>
			<komit-login-view-container
				.imageType="${FormWrapperImageType.Lady3}"
				.title="${title}"
				.subtitle="${subtitle}"
				.backendErrors="${this.backendErrors}"
				@backendErrorsUpdated="${this.updateBackendErrors}"
			>
				${action}
			</komit-login-view-container>
		`;
	}

	private loginFormTmpl() {
		return html`
			<style>
				${tailwind}
			</style>
			<komit-login-view-container
				.imageType="${FormWrapperImageType.Lady3}"
				.title="${util.tr("Welcome back! Glad to have you here.")}"
				.subtitle="${util.tr("Please log in to your account.")}"
			>
				<form
					id="loginForm"
					@submit="${this.login}"
					class="flex flex-col gap-8"
					action="#"
					method="POST"
				>
					<div class="flex flex-col gap-2">
						<label
							for="email"
							class="text-base leading-6 text-urban-night-900"
						>
							${util.tr("Email address")}
						</label>
						<input
							id="email"
							name="email"
							type="email"
							autocomplete="email"
							required
							autofocus
							class="w-full px-4 py-3 border border-urban-night-200 rounded-xl bg-white ktw-p-small-primary"
						/>
					</div>

					<div class="flex flex-col gap-2">
						<label
							for="password"
							class="text-base leading-6 text-urban-night-900"
						>
							${util.tr("Password")}
						</label>
						<input
							id="password"
							name="password"
							type="password"
							autocomplete="current-password"
							required
							class="w-full px-4 py-3 border border-urban-night-200 rounded-xl bg-white ktw-p-small-primary"
						/>
						<a
							@click="${this.openPasswordForgottenDialog}"
							class="inline-block w-fit cursor-pointer  text-base leading-6 text-urban-night-900 underline"
						>
							${util.tr("Forgot your password?")}
						</a>
					</div>

					<div class="pt-1 text-left">
						<button
							class="cursor-pointer w-full px-4 py-3 bg-blue-power-900 hover:bg-blue-power-800 rounded-xl text-xl font-semibold text-white"
							type="submit"
						>
							${util.tr("Sign in")}
						</button>
					</div>
				</form>
			</komit-login-view-container>
			<komit-dialog
				@closing=${this.closingDialog}
				?open=${this.isOpen}
				id="dialog"
				heading="${util.tr("Forgot your password?")}"
				narrow
			>
				<div>
					<label
						for="email"
						class="text-base leading-6 text-urban-night-900"
					>
						${util.tr("Email address")}
					</label>
					<input
						id="resetPasswordEmail"
						name="resetPasswordEmail"
						type="email"
						autocomplete="email"
						required
						class="block w-full rounded-md border-urban-night-200 shadow-xs focus:border-teal-500 focus:ring-teal-500"
					/>
				</div>
				<komit-button-new
					@click=${this.requestNewPassword}
					slot="primaryAction"
					variant="primary"
				>
					${util.tr("Request new password")}
				</komit-button-new>
				<komit-button-new
					dialogAction="close"
					slot="secondaryAction"
					variant="secondary"
				>
					${util.tr("Close")}
				</komit-button-new>
			</komit-dialog>

			<komit-login-second-factor-choice-dialog
				id="secondFactorChoiceDialog"
				.isTotpAvailable="${this.isTotpAvailable}"
				.isEmailMagicLinkAvailable="${this.isEmailMagicLinkAvailable}"
				@loginSecondFactorChosen="${this.handleSecondFactorChosen}"
			></komit-login-second-factor-choice-dialog>

			<komit-login-two-factor-code-dialog
				id="twoFactorCodeDialog"
				@loginTwoFactorAuthCodeSubmitted="${this
					.handleTwoFactorAuthCodeSubmitted}"
				@loginTwoFactorAuthResetRequested="${this
					.handleTwoFactorAuthResetRequested}"
			></komit-login-two-factor-code-dialog>

			<!-- the link that opens this sits in the two-factor dialog, but the
			element stays out here: it renders a non-modal dialog of its own, and
			inside the other one it would be taken down together with it -->
			<komit-request-two-factor-auth-reset-action
				id="twoFactorAuthResetAction"
			></komit-request-two-factor-auth-reset-action>
		`;
	}
}
